import * as React from "react"
import { Toaster as Sonner, toast } from "sonner"

/**
 * The Verlo toast: a navy card, white text, no icon, and a 4px rail only when it's a
 * signal (moss for success, claret for an error, copper for a warning). One line, active
 * voice. Three seconds; errors stay twice as long.
 *
 * The whole card is the tap target: a tap anywhere dismisses it. The till is a touch screen
 * in a busy room; a 20px × in the corner is not a thing a server hits first time, and a
 * toast that lingers over the pad is worse than none. The close button stays for a mouse.
 *
 * Where it sits is the app's call (`position`): bottom-centre on the till, clear of the
 * order pad and the keypad; bottom-right in the back office. Everything else is here, so
 * the 400 `toast.*` calls across both apps look the same without touching one of them.
 */
// The rail is a border, not a shadow, so it never fights `shadow-float`.
const RAIL = {
  success: "border-l-4 border-l-signal-ok-line",
  error: "border-l-4 border-l-signal-stop-line",
  warning: "border-l-4 border-l-signal-warn-line",
}

/** Tap anywhere on a toast to dismiss it. Sonner puts no id in the DOM, so the tap is
 *  forwarded to the card's own close button — the one path that removes a toast at once,
 *  with no animation frame in between (a till's screen is not always painting). A tap on an
 *  action, or on a card already on its way out, is left alone. */
const TapToDismiss = ({ children }) => {
  const onClick = (e) => {
    const card = e.target.closest?.("[data-sonner-toast]")
    if (!card || card.getAttribute("data-removed") === "true") return
    if (e.target.closest("[data-button], [data-close-button], a, input, select, textarea")) return
    card.querySelector("[data-close-button]")?.click()
  }
  return <div onClick={onClick}>{children}</div>
}

const Toaster = ({ position = "bottom-right", ...props }) => (
  <TapToDismiss>
  <Sonner
    theme="light"
    position={position}
    closeButton
    duration={3000}
    gap={8}
    offset={16}
    icons={{ success: null, error: null, warning: null, info: null, loading: null }}
    className="toaster group"
    toastOptions={{
      unstyled: true,
      classNames: {
        toast:
          "group toast flex w-[356px] cursor-pointer select-none items-center gap-2.5 bg-verlo py-2.5 pl-3.5 pr-3 font-body text-[13.5px] font-medium leading-snug text-white shadow-float",
        title: "font-medium",
        description: "text-white/75 text-[12.5px]",
        actionButton: "ml-auto shrink-0 font-bold text-white underline underline-offset-[3px] opacity-85 hover:opacity-100",
        cancelButton: "ml-auto shrink-0 text-white/75 hover:text-white",
        closeButton:
          "!static !order-last !ml-auto !h-5 !w-5 !translate-x-0 !translate-y-0 !border-0 !bg-transparent !text-white/60 hover:!bg-white/10 hover:!text-white",
        success: RAIL.success,
        error: RAIL.error,
        warning: RAIL.warning,
      },
    }}
    {...props}
  />
  </TapToDismiss>
)

// Errors stay twice as long: they need reading, and a till is a busy place.
const base = toast.error
const error = (message, options) => base(message, { duration: 6000, ...options })
toast.error = error

export { Toaster, toast }
